/**
 * Submit Organization Modal
 *
 * Form modal for submitting new organizations.
 * Features:
 * - Required fields: name, website, city, postal code, services, sector
 * - Optional fields: type, description
 * - Conditional claim fields ("I represent this organization")
 * - Conditional ticker fields ("We have an announcement")
 * - Form validation with error messages
 */

import { useState, useRef, useEffect } from 'react';
import { X, CaretDown, CheckCircle, CircleNotch, WarningCircle } from '@phosphor-icons/react';
import { COLORS, SECTORS, SERVICE_TAGS } from '../../lib/constants';
import { isValidEmail, isValidUrl, isValidDutchPostalCode } from '../../lib/validation';
import { modalStyles, getCloseButtonStyle } from './styles';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface FormData {
  name: string;
  website: string;
  city: string;
  postalCode: string;
  services: string[];
  sector: string;
  type: string;
  description: string;
  // Claim fields
  contactName: string;
  contactEmail: string;
  contactRole: string;
  // Announcement fields
  headline: string;
  announcementType: string;
  announcementLink: string;
}

interface ValidationErrors {
  name?: string;
  website?: string;
  city?: string;
  postalCode?: string;
  services?: string;
  sector?: string;
  // Conditional claim fields
  contactName?: string;
  contactEmail?: string;
  // Conditional announcement fields
  headline?: string;
  announcementType?: string;
}

const initialFormData: FormData = {
  name: '',
  website: '',
  city: '',
  postalCode: '',
  services: [],
  sector: '',
  type: '',
  description: '',
  contactName: '',
  contactEmail: '',
  contactRole: '',
  headline: '',
  announcementType: '',
  announcementLink: '',
};

interface SubmitOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Component-specific styles extending shared modal styles
const styles = {
  ...modalStyles,
  modal: {
    ...modalStyles.modal,
    maxWidth: '560px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: COLORS.graphite,
    cursor: 'pointer',
  },
  sectionDivider: {
    borderTop: `1px solid ${COLORS.graphite}15`,
    paddingTop: '16px',
    marginTop: '4px',
  },
  sectionTitle: {
    fontSize: '14px',
    color: COLORS.graphite,
    marginBottom: '12px',
  },
  selectWrapper: {
    position: 'relative' as const,
  },
  selectIcon: {
    position: 'absolute' as const,
    right: '12px',
    top: '12px',
    color: COLORS.graphite,
    opacity: 0.5,
    pointerEvents: 'none' as const,
  },
  selectedTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '8px',
  },
  selectedTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    backgroundColor: `${COLORS.royalBlue}15`,
    color: COLORS.royalBlue,
    borderRadius: 0,
    border: `1px solid ${COLORS.royalBlue}30`,
  },
  tagRemoveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.royalBlue,
    cursor: 'pointer',
    opacity: 0.7,
  },
};

export function SubmitOrganizationModal({
  isOpen,
  onClose,
}: SubmitOrganizationModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [iRepresent, setIRepresent] = useState(false);
  const [hasAnnouncement, setHasAnnouncement] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);
  // Ref to prevent double-click submissions (synchronous check before async state update)
  const isSubmittingRef = useRef(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Reset state and close
        setFormData(initialFormData);
        setErrors({});
        setIRepresent(false);
        setHasAnnouncement(false);
        setSubmitStatus('idle');
        setSubmitError('');
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Naam organisatie is verplicht';
    }

    if (!formData.website.trim()) {
      newErrors.website = 'Website is verplicht';
    } else if (!isValidUrl(formData.website)) {
      newErrors.website = 'Voer een geldige URL in (bijv. https://voorbeeld.nl)';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'Stad is verplicht';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postcode is verplicht';
    } else if (!isValidDutchPostalCode(formData.postalCode)) {
      newErrors.postalCode = 'Voer een geldige Nederlandse postcode in (bijv. 1234 AB)';
    }

    if (formData.services.length === 0) {
      newErrors.services = 'Selecteer minimaal één dienst';
    }

    if (!formData.sector) {
      newErrors.sector = 'Selecteer een sector';
    }

    // Conditional claim fields
    if (iRepresent) {
      if (!formData.contactName.trim()) {
        newErrors.contactName = 'Uw naam is verplicht';
      }
      if (!formData.contactEmail.trim()) {
        newErrors.contactEmail = 'Uw e-mail is verplicht';
      } else if (!isValidEmail(formData.contactEmail)) {
        newErrors.contactEmail = 'Voer een geldig e-mailadres in';
      }
    }

    // Conditional announcement fields
    if (hasAnnouncement) {
      if (!formData.headline.trim()) {
        newErrors.headline = 'Headline is verplicht';
      }
      if (!formData.announcementType) {
        newErrors.announcementType = 'Selecteer een type aankondiging';
      }
    }

    return newErrors;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear the error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value && !formData.services.includes(value)) {
      const newServices = [...formData.services, value];
      setFormData(prev => ({ ...prev, services: newServices }));
      // Clear the services error when a service is selected
      if (errors.services) {
        setErrors(prev => ({ ...prev, services: undefined }));
      }
    }
    // Reset select to placeholder
    e.target.value = '';
  };

  const handleRemoveService = (serviceToRemove: string) => {
    const newServices = formData.services.filter(s => s !== serviceToRemove);
    setFormData(prev => ({ ...prev, services: newServices }));
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double-click submissions using synchronous ref check
    // This prevents race conditions where multiple clicks happen before state updates
    if (isSubmittingRef.current) {
      return;
    }

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Mark as submitting immediately (synchronous) to prevent double-clicks
    isSubmittingRef.current = true;

    // Submit to API
    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const response = await fetch(`${API_BASE_URL}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          website: formData.website,
          city: formData.city,
          postalCode: formData.postalCode,
          services: formData.services,
          sector: formData.sector,
          type: formData.type || undefined,
          description: formData.description || undefined,
          iRepresent,
          contactName: iRepresent ? formData.contactName : undefined,
          contactEmail: iRepresent ? formData.contactEmail : undefined,
          contactRole: iRepresent ? formData.contactRole : undefined,
          hasAnnouncement,
          headline: hasAnnouncement ? formData.headline : undefined,
          announcementType: hasAnnouncement ? formData.announcementType : undefined,
          announcementLink: hasAnnouncement ? formData.announcementLink : undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Er is een fout opgetreden');
      }

      setSubmitStatus('success');
      // Reset ref on success (form will show success view)
      isSubmittingRef.current = false;
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
      // Always show user-friendly message, never expose technical details
      setSubmitError('Er is een fout opgetreden bij het aanmelden. Probeer het later opnieuw.');
      // Reset ref on error so user can retry
      isSubmittingRef.current = false;
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setFormData(initialFormData);
    setErrors({});
    setIRepresent(false);
    setHasAnnouncement(false);
    setSubmitStatus('idle');
    setSubmitError('');
    // Reset double-click prevention ref
    isSubmittingRef.current = false;
    onClose();
  };

  // Success view
  if (submitStatus === 'success') {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div style={styles.header}>
            <h2 id="modal-title" style={styles.title}>Organisatie aanmelden</h2>
            <button
              style={getCloseButtonStyle(closeHovered)}
              onClick={handleClose}
              aria-label="Sluiten"
              onMouseEnter={() => setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div style={styles.statusContainer}>
            <CheckCircle size={64} weight="fill" color="#22c55e" style={styles.statusIcon} />
            <h3 style={styles.statusTitle}>Bedankt voor uw aanmelding!</h3>
            <p style={styles.statusMessage}>
              Uw organisatie is succesvol aangemeld. We zullen uw aanvraag zo snel mogelijk bekijken.
              Na goedkeuring verschijnt uw organisatie op de kaart.
            </p>
            <button style={styles.statusButton} onClick={handleClose}>
              Sluiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error view
  if (submitStatus === 'error') {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div style={styles.header}>
            <h2 id="modal-title" style={styles.title}>Organisatie aanmelden</h2>
            <button
              style={getCloseButtonStyle(closeHovered)}
              onClick={handleClose}
              aria-label="Sluiten"
              onMouseEnter={() => setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div style={styles.statusContainer}>
            <WarningCircle size={64} weight="fill" color="#dc2626" style={styles.statusIcon} />
            <h3 style={styles.statusTitle}>Er is iets misgegaan</h3>
            <p style={styles.statusMessage}>
              {submitError || 'Er is een fout opgetreden bij het aanmelden. Probeer het later opnieuw.'}
            </p>
            <button style={styles.statusButton} onClick={() => setSubmitStatus('idle')}>
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div style={styles.header}>
          <h2 id="modal-title" style={styles.title}>Organisatie aanmelden</h2>
          <button
            style={{
              ...styles.closeButton,
              backgroundColor: closeHovered ? `${COLORS.graphite}10` : 'transparent',
            }}
            onClick={handleClose}
            aria-label="Sluiten"
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div style={styles.content}>
          <form style={styles.form} onSubmit={handleSubmit} ref={formRef}>
            {/* Required Fields */}
            <div style={styles.fieldGroup} data-field="name">
              <label htmlFor="submit-org-name" style={styles.label}>
                Naam organisatie<span style={styles.required}>*</span>
              </label>
              <input
                id="submit-org-name"
                type="text"
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {}),
                }}
                placeholder="Bijv. Dembrane"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <span id="name-error" style={styles.errorText} role="alert">
                  {errors.name}
                </span>
              )}
            </div>

            <div style={styles.fieldGroup} data-field="website">
              <label htmlFor="submit-org-website" style={styles.label}>
                Website<span style={styles.required}>*</span>
              </label>
              <input
                id="submit-org-website"
                type="text"
                style={{
                  ...styles.input,
                  ...(errors.website ? styles.inputError : {}),
                }}
                placeholder="https://voorbeeld.nl"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                aria-invalid={!!errors.website}
                aria-describedby={errors.website ? 'website-error' : undefined}
              />
              {errors.website && (
                <span id="website-error" style={styles.errorText} role="alert">
                  {errors.website}
                </span>
              )}
            </div>

            <div style={styles.row}>
              <div style={styles.fieldGroup} data-field="city">
                <label htmlFor="submit-org-city" style={styles.label}>
                  Stad<span style={styles.required}>*</span>
                </label>
                <input
                  id="submit-org-city"
                  type="text"
                  style={{
                    ...styles.input,
                    ...(errors.city ? styles.inputError : {}),
                  }}
                  placeholder="Bijv. Amsterdam"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  aria-invalid={!!errors.city}
                  aria-describedby={errors.city ? 'city-error' : undefined}
                />
                {errors.city && (
                  <span id="city-error" style={styles.errorText} role="alert">
                    {errors.city}
                  </span>
                )}
              </div>
              <div style={styles.fieldGroup} data-field="postalCode">
                <label htmlFor="submit-org-postalCode" style={styles.label}>
                  Postcode<span style={styles.required}>*</span>
                </label>
                <input
                  id="submit-org-postalCode"
                  type="text"
                  style={{
                    ...styles.input,
                    ...(errors.postalCode ? styles.inputError : {}),
                  }}
                  placeholder="1234 AB"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  aria-invalid={!!errors.postalCode}
                  aria-describedby={errors.postalCode ? 'postalCode-error' : undefined}
                />
                {errors.postalCode && (
                  <span id="postalCode-error" style={styles.errorText} role="alert">
                    {errors.postalCode}
                  </span>
                )}
              </div>
            </div>

            <div style={styles.fieldGroup} data-field="services">
              <label htmlFor="submit-org-services" style={styles.label}>
                Diensten/services<span style={styles.required}>*</span>
              </label>
              <div style={styles.selectWrapper}>
                <select
                  id="submit-org-services"
                  style={{
                    ...styles.select,
                    width: '100%',
                    paddingRight: '36px',
                    ...(errors.services ? styles.inputError : {}),
                  }}
                  onChange={handleServiceSelect}
                  defaultValue=""
                  aria-invalid={!!errors.services}
                  aria-describedby={errors.services ? 'services-error' : undefined}
                  data-testid="services-multi-select"
                >
                  <option value="" disabled>
                    Selecteer diensten...
                  </option>
                  {SERVICE_TAGS.map((tag) => (
                    <option
                      key={tag}
                      value={tag}
                      disabled={formData.services.includes(tag)}
                    >
                      {tag}
                    </option>
                  ))}
                </select>
                <CaretDown size={14} style={styles.selectIcon} weight="bold" />
              </div>
              {formData.services.length > 0 && (
                <div style={styles.selectedTagsContainer}>
                  {formData.services.map((service) => (
                    <span key={service} style={styles.selectedTag}>
                      {service}
                      <button
                        type="button"
                        style={styles.tagRemoveButton}
                        onClick={() => handleRemoveService(service)}
                        aria-label={`Verwijder ${service}`}
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {errors.services && (
                <span id="services-error" style={styles.errorText} role="alert">
                  {errors.services}
                </span>
              )}
            </div>

            <div style={styles.fieldGroup} data-field="sector">
              <label htmlFor="submit-org-sector" style={styles.label}>
                Sector<span style={styles.required}>*</span>
              </label>
              <select
                id="submit-org-sector"
                style={{
                  ...styles.select,
                  ...(errors.sector ? styles.inputError : {}),
                }}
                value={formData.sector}
                onChange={(e) => handleInputChange('sector', e.target.value)}
                aria-invalid={!!errors.sector}
                aria-describedby={errors.sector ? 'sector-error' : undefined}
              >
                <option value="">Selecteer sector</option>
                {SECTORS.map((sector) => (
                  <option key={sector.value} value={sector.value}>
                    {sector.label}
                  </option>
                ))}
              </select>
              {errors.sector && (
                <span id="sector-error" style={styles.errorText} role="alert">
                  {errors.sector}
                </span>
              )}
            </div>

            {/* Optional Fields */}
            <div style={styles.sectionDivider}>
              <div style={styles.sectionTitle}>Optionele informatie</div>
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="submit-org-type" style={styles.label}>Type organisatie</label>
              <select
                id="submit-org-type"
                style={styles.select}
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <option value="">Selecteer type</option>
                <option value="stichting">Stichting</option>
                <option value="bv">BV</option>
                <option value="adviesbureau">Adviesbureau</option>
                <option value="opleidingsinstituut">Opleidingsinstituut</option>
                <option value="overig">Overig</option>
              </select>
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="submit-org-description" style={styles.label}>Beschrijving</label>
              <textarea
                id="submit-org-description"
                style={styles.textarea}
                placeholder="Korte beschrijving van de organisatie..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </div>

            {/* Claim Checkbox */}
            <div style={styles.sectionDivider}>
              <div style={styles.checkboxGroup}>
                <input
                  type="checkbox"
                  id="represent"
                  style={styles.checkbox}
                  checked={iRepresent}
                  onChange={(e) => setIRepresent(e.target.checked)}
                />
                <label htmlFor="represent" style={styles.checkboxLabel}>
                  Ik vertegenwoordig deze organisatie
                </label>
              </div>
            </div>

            {iRepresent && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={styles.fieldGroup} data-field="contactName">
                  <label htmlFor="submit-org-contactName" style={styles.label}>
                    Uw naam<span style={styles.required}>*</span>
                  </label>
                  <input
                    id="submit-org-contactName"
                    type="text"
                    style={{
                      ...styles.input,
                      ...(errors.contactName ? styles.inputError : {}),
                    }}
                    placeholder="Uw volledige naam"
                    value={formData.contactName}
                    onChange={(e) => handleInputChange('contactName', e.target.value)}
                    aria-invalid={!!errors.contactName}
                    aria-describedby={errors.contactName ? 'contactName-error' : undefined}
                  />
                  {errors.contactName && (
                    <span id="contactName-error" style={styles.errorText} role="alert">
                      {errors.contactName}
                    </span>
                  )}
                </div>
                <div style={styles.fieldGroup} data-field="contactEmail">
                  <label htmlFor="submit-org-contactEmail" style={styles.label}>
                    Uw e-mail<span style={styles.required}>*</span>
                  </label>
                  <input
                    id="submit-org-contactEmail"
                    type="email"
                    style={{
                      ...styles.input,
                      ...(errors.contactEmail ? styles.inputError : {}),
                    }}
                    placeholder="uw@email.nl"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    aria-invalid={!!errors.contactEmail}
                    aria-describedby={errors.contactEmail ? 'contactEmail-error' : undefined}
                  />
                  {errors.contactEmail && (
                    <span id="contactEmail-error" style={styles.errorText} role="alert">
                      {errors.contactEmail}
                    </span>
                  )}
                </div>
                <div style={styles.fieldGroup}>
                  <label htmlFor="submit-org-contactRole" style={styles.label}>Uw functie</label>
                  <input
                    id="submit-org-contactRole"
                    type="text"
                    style={styles.input}
                    placeholder="Bijv. Directeur"
                    value={formData.contactRole}
                    onChange={(e) => handleInputChange('contactRole', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Announcement Checkbox */}
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="announcement"
                style={styles.checkbox}
                checked={hasAnnouncement}
                onChange={(e) => setHasAnnouncement(e.target.checked)}
              />
              <label htmlFor="announcement" style={styles.checkboxLabel}>
                Wij hebben een aankondiging om te delen
              </label>
            </div>

            {hasAnnouncement && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={styles.fieldGroup} data-field="headline">
                  <label htmlFor="submit-org-headline" style={styles.label}>
                    Headline<span style={styles.required}>*</span>
                  </label>
                  <input
                    id="submit-org-headline"
                    type="text"
                    style={{
                      ...styles.input,
                      ...(errors.headline ? styles.inputError : {}),
                    }}
                    placeholder="Korte aankondiging"
                    value={formData.headline}
                    onChange={(e) => handleInputChange('headline', e.target.value)}
                    aria-invalid={!!errors.headline}
                    aria-describedby={errors.headline ? 'headline-error' : undefined}
                  />
                  {errors.headline && (
                    <span id="headline-error" style={styles.errorText} role="alert">
                      {errors.headline}
                    </span>
                  )}
                </div>
                <div style={styles.fieldGroup} data-field="announcementType">
                  <label htmlFor="submit-org-announcementType" style={styles.label}>
                    Type aankondiging<span style={styles.required}>*</span>
                  </label>
                  <select
                    id="submit-org-announcementType"
                    style={{
                      ...styles.select,
                      ...(errors.announcementType ? styles.inputError : {}),
                    }}
                    value={formData.announcementType}
                    onChange={(e) => handleInputChange('announcementType', e.target.value)}
                    aria-invalid={!!errors.announcementType}
                    aria-describedby={errors.announcementType ? 'announcementType-error' : undefined}
                  >
                    <option value="">Selecteer type</option>
                    <option value="vacancy">Vacature</option>
                    <option value="open_call">Open call</option>
                    <option value="subsidy">Subsidie</option>
                    <option value="other">Anders</option>
                  </select>
                  {errors.announcementType && (
                    <span id="announcementType-error" style={styles.errorText} role="alert">
                      {errors.announcementType}
                    </span>
                  )}
                </div>
                <div style={styles.fieldGroup}>
                  <label htmlFor="submit-org-announcementLink" style={styles.label}>Link</label>
                  <input
                    id="submit-org-announcementLink"
                    type="url"
                    style={styles.input}
                    placeholder="https://voorbeeld.nl/aankondiging"
                    value={formData.announcementLink}
                    onChange={(e) => handleInputChange('announcementLink', e.target.value)}
                  />
                </div>
              </div>
            )}
          </form>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={handleClose} disabled={submitStatus === 'submitting'}>
            Annuleren
          </button>
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              ...(submitStatus === 'submitting' ? styles.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={submitStatus === 'submitting'}
          >
            {submitStatus === 'submitting' ? (
              <>
                <CircleNotch size={16} weight="bold" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                Bezig met aanmelden...
              </>
            ) : (
              'Aanmelden'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

